import { useContext } from 'react'

import {
  Button, Form, InputEnhanced, Line, ModalContext, SidebarModal, Text,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import PropertySuggestion from './components/PropertySuggestion'
import useSearchCampaignComparables from './hooks/useSearchCampaignComparables'

const MODAL_KEY = 'SearchCampaignComparablesModal'

type ModalCallbacks = {
  closeModal: () => void,
  createComparableFromDataProvider: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const SearchCampaignComparablesModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, createComparableFromDataProvider } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const { campaignComparable } = digObject(modalData, MODAL_KEY, {})

  const useSearchCampaignComparablesPayload = useSearchCampaignComparables({ campaignId: campaignComparable.id })

  const {
    agencyResults,
    callbacks: {
      combinedSearch,
      setModalState,
    },
    filteredCampaignComparables,
    hasAgencyResults,
    hasMarketResults,
    marketResults,
    searchString,
    searchedString,
    searching,
  } = useSearchCampaignComparablesPayload

  const resultsCount = agencyResults.length + marketResults.length
  const isSearchable = searchString !== searchedString && searchString !== ''

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header
        callbacks={callbacks}
        title="Add"
        titleSecondLine="Comparable"
      />

      <SidebarModal.Content>
        <Form>
          <Form.Field
            label="Property Search"
          >
            <InputEnhanced
              keyPressCallbacks={[
                {
                  eventKey: 'Enter',
                  // @ts-expect-error TS18048
                  callback: e => combinedSearch(e.target.value),
                },
              ]}
              onChange={e => setModalState({ searchString: e.target.value })}
              type="text"
              value={searchString}
            />
          </Form.Field>

          {searchedString && (
            <Text color="bodyFontLightColor" fontSize="xsmall" marginTop="large">
              <strong>{resultsCount}&nbsp;</strong>
              found that {resultsCount === 1 ? 'is' : 'are'} a close match to
              <strong>&nbsp;{searchedString}</strong>
            </Text>
          )}

          {!searching && hasMarketResults && (
            <>
              <Text
                color="bodyFontLightColor"
                fontSize="xsmall"
                marginBottom="5px"
                marginTop="large"
              >
                Market Data Properties
              </Text>

              {marketResults.map(suggestion => (
                <PropertySuggestion
                  added={filteredCampaignComparables.some(
                    comparable => comparable.data && comparable.data.id === suggestion.id,
                  )}
                  callbacks={{ createComparableFromDataProvider }}
                  campaignComparable={campaignComparable}
                  key={suggestion.id}
                  suggestion={suggestion}
                />
              ))}
            </>
          )}

          {!searching && hasAgencyResults && hasMarketResults && <Line />}

          {!searching && hasAgencyResults && (
            <>
              <Text
                color="bodyFontLightColor"
                fontSize="xsmall"
                marginBottom="5px"
                marginTop="large"
              >
                Your Agency Properties
              </Text>

              {agencyResults.map(suggestion => (
                <PropertySuggestion
                  added={filteredCampaignComparables.some(
                    comparable => comparable.source_campaign_id === suggestion.id,
                  )}
                  callbacks={{ createComparableFromDataProvider }}
                  campaignComparable={campaignComparable}
                  key={suggestion.id}
                  suggestion={suggestion}
                />
              ))}
            </>
          )}
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle={isSearchable ? 'primaryCreate' : 'secondaryUtility'}
          loading={searching}
          // @ts-expect-error TS2322
          searchingBubbleColor="white"
          onClick={isSearchable ? () => combinedSearch(searchString) : () => closeModal()}
          size="large"
        >
          {isSearchable ? 'Search Comparables' : 'Close Modal'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <SearchCampaignComparablesModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
